import React, { useState } from 'react';
import CommonModal from '../Components/CommonModal';
import { change_password } from '../services';
import CommonButton from '../Components/CommonButton';
import { lightTheme } from '../Constants/Colors';
import { subHeading1 } from '../Constants/Fonts';

const PASSWORD_REGEX = /^(?=.{8,}$)(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()]).*$/;
const PASSWORD_RULE_MSG = 'Password must be 8+ characters, include uppercase, lowercase, number & special character';

// Human-readable form of PASSWORD_REGEX, surfaced on the card so users see the
// policy before opening the modal (previously it appeared only after a failed submit).
const PASSWORD_RULES = [
  'Minimum 8 characters',
  'One uppercase letter (A–Z)',
  'One lowercase letter (a–z)',
  'One number (0–9)',
  'One special character (! @ # $ % ^ & *)',
];

function SecuritySettings() {
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  // Common toast — same pattern used across the app for API responses
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 4000);
  };

  const openModal = () => {
    setApiError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setApiError('');
    setShowModal(false);
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    setApiError('');
    try {
      const params = {
        old_password: data.oldPassword,
        new_password: data.newPassword,
        confirm_password: data.confirmNewPassword,
      };

      const response = await change_password(params);
      const successMessage = response?.statusCode?.response_data?.data?.message;
      const errorMessage = response?.errors?.message;

      if (successMessage) {
        showToast(successMessage, 'success');
        setShowModal(false);
      } else if (errorMessage) {
        setApiError(errorMessage);
        showToast(errorMessage, 'error');
      } else {
        const fallback = 'Something went wrong while changing the password.';
        setApiError(fallback);
        showToast(fallback, 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      const msg = error?.response?.data?.errors?.message || 'Something went wrong while changing the password.';
      setApiError(msg);
      showToast(msg, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const passwordFields = [
    {
      name: 'oldPassword',
      label: 'Old Password',
      type: 'password',
      placeholder: 'Type old password',
      required: 'Old password is required',
      maxLength: { value: 30, message: 'Maximum 30 characters' },
      pattern: { value: PASSWORD_REGEX, message: PASSWORD_RULE_MSG },
    },
    {
      name: 'newPassword',
      label: 'New Password',
      type: 'password',
      placeholder: 'Type new password',
      required: 'New password is required',
      maxLength: { value: 30, message: 'Maximum 30 characters' },
      pattern: { value: PASSWORD_REGEX, message: PASSWORD_RULE_MSG },
      validate: (value, all) => value !== all.oldPassword || 'New password must be different from old password',
    },
    {
      name: 'confirmNewPassword',
      label: 'Confirm New Password',
      type: 'password',
      placeholder: 'Re-type new password',
      required: 'Confirm new password is required',
      maxLength: { value: 30, message: 'Maximum 30 characters' },
      validate: (value, all) => value === all.newPassword || 'Confirm Passwords do not match',
    },
  ];

  return (
    <div>
      {/* Change Password Section */}
      <div className='d-flex gap-3 mb-3 align-items-center'>
        <img src='https://nmtsecurity-publicbucket.s3.us-west-2.amazonaws.com/dashboard/external-attack-surface-scan.svg' alt='external-attack-surface-scan' height={32} width={32} />
        <h3 className='mb-0' style={subHeading1({ color: lightTheme.textPrimary })}>Change Password</h3>
      </div>

      <div className="companyProfileContainer">
        <div className="security_panel">
          <div className="security_row">
            <span className="security_row_icon">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
                <path d="M17 8h-1V6a4 4 0 0 0-8 0v2H7a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2Zm-7-2a2 2 0 0 1 4 0v2h-4V6Zm7 13H7v-9h10v9Zm-5-2.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z" />
              </svg>
            </span>

            <div className="security_row_body">
              <p className="security_row_title">Password</p>
              <p className="security_row_desc">Choose a unique password to protect your account</p>
            </div>

            <CommonButton text="Change" onClick={openModal} />
          </div>

          <div className="security_policy">
            <p className="security_policy_title">Password requirements</p>
            <ul className="security_policy_list">
              {PASSWORD_RULES.map((rule) => (
                <li key={rule}>{rule}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* MFA Section */}
      {/* <div className='d-flex gap-3 mt-3 mb-3 align-items-center'>
        <img src='https://nmtsecurity-publicbucket.s3.us-west-2.amazonaws.com/dashboard/external-attack-surface-scan.svg' alt='external-attack-surface-scan' height={32} width={32} />
        <h3 className='color_white mb-0 font_20 font-600'>Multi-Factor Authentication</h3>
      </div>

      <div className="companyProfileContainer">
        <section className="companyProfile">
          <div className='infoGrid pt-0'>
            <div>
              <p className='mb-0 font_14 color_light_blue'>Multi-Factor Authentication</p>
              <p className='mb-0 gap-2 font_20 d-flex'>Two-Step Verification</p>
            </div>
            <div>
              <p className='mb-0 mt-4 font_20 font_500 blue_text'>Change</p>
            </div>
          </div>
        </section>
      </div> */}

      {/* Change Password Modal — common component with react-hook-form validation */}
      <CommonModal
        show={showModal}
        onHide={closeModal}
        title="Change Password"
        subtitle="Choose a unique password to protect your account."
        fields={passwordFields}
        defaultValues={{ oldPassword: '', newPassword: '', confirmNewPassword: '' }}
        onSubmit={onSubmit}
        error={apiError}
        buttons={[
          { text: 'Cancel', variant: 'secondary', onClick: closeModal, disabled: isLoading },
          { text: 'Submit', variant: 'primary', type: 'submit', loading: isLoading, loadingText: 'Submitting…' },
        ]}
      />

      {/* Toast — common alert pattern */}
      {toast.show && (
        <div className={`custom-toast ${toast.type}`}>
          <span className="icon">{toast.type === 'success' ? '✓' : '✕'}</span>
          <span className="message">{toast.message}</span>
          <button
            className="close-btn"
            onClick={() => setToast({ show: false, message: '', type: 'success' })}
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}

export default SecuritySettings;
